import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    marginBottom: 10,
    background: "#fff",
  },
  header: (bg) => ({
    background: bg,
    color: "#fff",
    padding: "8px 12px",
  }),
  title: { fontSize: 12, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  statCard: {
    borderRadius: 10,
    padding: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    height: "100%",
  },
  statTitle: { fontSize: 10, color: "#6c757d", marginBottom: 3 },
  statValue: { fontSize: 14, fontWeight: 700, color: "#0d6efd" },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

const get = (obj, pascal, camel) => {
  const value = obj?.[pascal] ?? obj?.[camel] ?? "";

  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";

  return value;
};

const num = (v) => {
  if (v === null || v === undefined || typeof v === "object") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) => num(v).toFixed(2);

const getInvoiceValue = (r) => {
  const grandTotal = num(get(r, "GrandTotal", "grandTotal"));
  const invoiceValue = num(get(r, "InvoiceValue", "invoiceValue"));

  if (grandTotal > 0) return grandTotal;
  if (invoiceValue > 0) return invoiceValue;

  return (
    num(get(r, "TaxableValue", "taxableValue")) +
    num(get(r, "CGST", "cgst")) +
    num(get(r, "SGST", "sgst")) +
    num(get(r, "IGST", "igst"))
  );
};

export default function GstReportsPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gstr1, setGstr1] = useState([]);
  const [gstr3b, setGstr3b] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReports = async () => {
    if (!fromDate || !toDate) {
      alert("Select from and to date");
      return;
    }

    try {
      setLoading(true);

      const params = { fromDate, toDate };

      const [r1, r3b] = await Promise.all([
        client.get("/gst-reports/gstr1", { params }),
        client.get("/gst-reports/gstr3b", { params }),
      ]);

      setGstr1(Array.isArray(r1.data) ? r1.data : []);
      setGstr3b(r3b.data || null);
    } catch (error) {
      console.error("GST Reports error:", error);

      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : "") ||
        error.message ||
        "Failed to load GST reports";

      alert(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const monthlyTrend = useMemo(() => {
    const map = {};

    gstr1.forEach((r) => {
      const rawDate = get(r, "InvoiceDate", "invoiceDate");
      const date = rawDate ? new Date(rawDate) : null;

      if (!date || Number.isNaN(date.getTime())) return;

      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}`;

      if (!map[key]) {
        map[key] = {
          month: key,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalTax: 0,
        };
      }

      const cgst = num(get(r, "CGST", "cgst"));
      const sgst = num(get(r, "SGST", "sgst"));
      const igst = num(get(r, "IGST", "igst"));

      map[key].cgst += cgst;
      map[key].sgst += sgst;
      map[key].igst += igst;
      map[key].totalTax += cgst + sgst + igst;
    });

    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [gstr1]);

  const totals = useMemo(() => {
    return {
      taxable: gstr1.reduce(
        (s, r) => s + num(get(r, "TaxableValue", "taxableValue")),
        0,
      ),
      cgst: gstr1.reduce((s, r) => s + num(get(r, "CGST", "cgst")), 0),
      sgst: gstr1.reduce((s, r) => s + num(get(r, "SGST", "sgst")), 0),
      igst: gstr1.reduce((s, r) => s + num(get(r, "IGST", "igst")), 0),
      invoice: gstr1.reduce((s, r) => s + getInvoiceValue(r), 0),
    };
  }, [gstr1]);

  const gstr3bSummary = useMemo(() => {
    const outputCGST = num(get(gstr3b, "OutputCGST", "outputCGST"));
    const outputSGST = num(get(gstr3b, "OutputSGST", "outputSGST"));
    const outputIGST = num(get(gstr3b, "OutputIGST", "outputIGST"));

    const outputTax =
      num(get(gstr3b, "OutputTax", "outputTax")) ||
      outputCGST + outputSGST + outputIGST;

    const inputTaxCredit = num(get(gstr3b, "InputTaxCredit", "inputTaxCredit"));

    const rawPayable =
      num(get(gstr3b, "NetTaxPayable", "netTaxPayable")) ||
      outputTax - inputTaxCredit;

    return {
      outwardTaxable: num(
        get(gstr3b, "OutwardTaxableValue", "outwardTaxableValue"),
      ),
      outputCGST,
      outputSGST,
      outputIGST,
      outputTax,
      inputTaxCredit,
      netTaxPayable: Math.max(rawPayable, 0),
      itcCarryForward:
        num(get(gstr3b, "ITCCarryForward", "itcCarryForward")) ||
        Math.max(-rawPayable, 0),
    };
  }, [gstr3b]);

  const exportGstr1Excel = () => {
    if (gstr1.length === 0) {
      alert("No GSTR-1 data");
      return;
    }

    const exportRows = gstr1.map((r) => ({
      InvoiceNo: get(r, "InvoiceNo", "invoiceNo"),
      InvoiceDate: String(get(r, "InvoiceDate", "invoiceDate")).slice(0, 10),
      CustomerName: get(r, "CustomerName", "customerName"),
      GSTNo: get(r, "GSTNo", "gstNo") || "-",
      SupplyType: get(r, "SupplyType", "supplyType"),
      TaxableValue: num(get(r, "TaxableValue", "taxableValue")),
      CGST: num(get(r, "CGST", "cgst")),
      SGST: num(get(r, "SGST", "sgst")),
      IGST: num(get(r, "IGST", "igst")),
      InvoiceValue: getInvoiceValue(r),
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "GSTR-1");

    if (gstr3b) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet([gstr3bSummary]),
        "GSTR-3B",
      );
    }

    XLSX.writeFile(wb, `GSTR1_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
          <h6 style={styles.title}>
            <i className="bi bi-receipt me-2"></i>
            GST Reports
          </h6>
          <div style={styles.sub}>GSTR-1, GSTR-3B, GST trends and exports</div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label style={styles.label}>From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label style={styles.label}>To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-6 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                style={styles.btn}
                onClick={loadReports}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>

              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={exportGstr1Excel}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-2">
        {[
          ["Taxable", totals.taxable],
          ["CGST", totals.cgst],
          ["SGST", totals.sgst],
          ["IGST", totals.igst],
          ["Invoice", totals.invoice],
        ].map(([label, value]) => (
          <div className="col-md-2" key={label}>
            <div style={styles.statCard}>
              <div style={styles.statTitle}>{label}</div>
              <div style={styles.statValue}>₹ {fmt(value)}</div>
            </div>
          </div>
        ))}
      </div>

      {gstr3b && (
        <div style={styles.card}>
          <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
            <h6 style={styles.title}>
              <i className="bi bi-calculator me-2"></i>
              GSTR-3B Summary
            </h6>
          </div>

          <div style={styles.body}>
            <table className="table table-sm table-bordered mb-0">
              <tbody>
                {[
                  ["Outward Taxable", gstr3bSummary.outwardTaxable],
                  ["Output CGST", gstr3bSummary.outputCGST],
                  ["Output SGST", gstr3bSummary.outputSGST],
                  ["Output IGST", gstr3bSummary.outputIGST],
                  ["Output Tax", gstr3bSummary.outputTax],
                  ["Input Tax Credit", gstr3bSummary.inputTaxCredit],
                  ["Net Tax Payable", gstr3bSummary.netTaxPayable],
                  ["ITC Carry Forward", gstr3bSummary.itcCarryForward],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <th style={styles.th}>{k}</th>
                    <td
                      className={`text-end ${
                        k === "Net Tax Payable"
                          ? "fw-bold text-success"
                          : k === "ITC Carry Forward"
                            ? "fw-bold text-primary"
                            : ""
                      }`}
                      style={styles.td}
                    >
                      ₹ {fmt(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
          <h6 style={styles.title}>
            <i className="bi bi-bar-chart me-2"></i>
            Monthly GST Trend
          </h6>
        </div>

        <div style={{ ...styles.body, height: 300 }}>
          {monthlyTrend.length === 0 ? (
            <div className="text-center text-muted mt-5">No chart data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyTrend}
                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip formatter={(value) => `₹ ${fmt(value)}`} />
                <Legend />
                <Bar
                  dataKey="cgst"
                  name="CGST"
                  fill="#0d6efd"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="sgst"
                  name="SGST"
                  fill="#0f766e"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="igst"
                  name="IGST"
                  fill="#fd7e14"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="totalTax"
                  name="Total GST"
                  fill="#6f42c1"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
          <h6 style={styles.title}>
            <i className="bi bi-table me-2"></i>
            GSTR-1 Outward Supplies
          </h6>
        </div>

        <div style={styles.body}>
          <div style={{ overflowX: "auto", maxHeight: 350 }}>
            <table className="table table-hover table-bordered table-sm mb-0">
              <thead className="sticky-top" style={{ zIndex: 1 }}>
                <tr>
                  {[
                    "Invoice",
                    "Date",
                    "Customer",
                    "GSTIN",
                    "Supply",
                    "Taxable",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Value",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {gstr1.length ? (
                  gstr1.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {get(r, "InvoiceNo", "invoiceNo")}
                      </td>
                      <td style={styles.td}>
                        {String(get(r, "InvoiceDate", "invoiceDate")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td style={styles.td}>
                        {get(r, "CustomerName", "customerName")}
                      </td>
                      <td style={styles.td}>
                        {get(r, "GSTNo", "gstNo") || "-"}
                      </td>
                      <td style={styles.td}>
                        {get(r, "SupplyType", "supplyType")}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "TaxableValue", "taxableValue"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "CGST", "cgst"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "SGST", "sgst"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "IGST", "igst"))}
                      </td>
                      <td className="text-end fw-semibold" style={styles.td}>
                        {fmt(getInvoiceValue(r))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No GST records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
